import { useMemo } from 'react';
import { MentorAvailabilityPayload, MentorAvailabilitySlot, MentorBookedSlot } from './MentorPortalService';
import { dayOfWeekFromDate } from './mentee/menteeSessionsFormat';

const DAYS: { dayOfWeek: number; label: string }[] = [
    { dayOfWeek: 1, label: 'Monday' },
    { dayOfWeek: 2, label: 'Tuesday' },
    { dayOfWeek: 3, label: 'Wednesday' },
    { dayOfWeek: 4, label: 'Thursday' },
    { dayOfWeek: 5, label: 'Friday' },
    { dayOfWeek: 6, label: 'Saturday' },
    { dayOfWeek: 7, label: 'Sunday' },
];

const DURATIONS = [30, 45, 60] as const;

/** Morning 09:00–12:00 (exclusive of 12:00) and afternoon 13:00–18:00; step = session duration. */
export const availabilityStartMinutesForDuration = (durationMinutes: number): number[] => {
    const step = Number(durationMinutes);
    if (![30, 45, 60].includes(step)) return [];
    const minutes: number[] = [];
    for (let minute = 540; minute < 720; minute += step) {
        minutes.push(minute);
    }
    for (let minute = 780; minute <= 1080; minute += step) {
        minutes.push(minute);
    }
    return minutes;
};

export const slotKey = (dayOfWeek: number, startMinute: number): string => `${dayOfWeek}:${startMinute}`;

export const formatClock = (startMinute: number): string => {
    const hour24 = Math.floor(startMinute / 60);
    const minute = startMinute % 60;
    const suffix = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
};

export const slotsEqual = (left: MentorAvailabilitySlot[], right: MentorAvailabilitySlot[]): boolean => {
    const a = [...left].map((slot) => slotKey(slot.dayOfWeek, slot.startMinute)).sort();
    const b = [...right].map((slot) => slotKey(slot.dayOfWeek, slot.startMinute)).sort();
    return a.length === b.length && a.every((key, index) => key === b[index]);
};

type Props = {
    durationMinutes: number;
    slots: MentorAvailabilitySlot[];
    saved: MentorAvailabilityPayload;
    bookedSlots?: MentorBookedSlot[];
    saveError: string | null;
    isSaving: boolean;
    onDurationChange: (value: number) => void;
    onToggle: (dayOfWeek: number, startMinute: number) => void;
    onSave: () => void;
    onDiscard: () => void;
};

const BANNER =
    'ℹ️ Click a cell to toggle Available ↔ Unavailable. Open slots are bookable by your mentees. Booked cells cannot be changed here.';

const MentorAvailabilityPanel = ({
    durationMinutes,
    slots,
    saved,
    bookedSlots = [],
    saveError,
    isSaving,
    onDurationChange,
    onToggle,
    onSave,
    onDiscard,
}: Props) => {
    const open = new Set(slots.map((slot) => slotKey(slot.dayOfWeek, slot.startMinute)));
    const bookedKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const row of bookedSlots) {
            if (!row.bookedDate || row.startMinute == null) continue;
            const dow = dayOfWeekFromDate(row.bookedDate);
            if (dow == null) continue;
            keys.add(slotKey(dow, row.startMinute));
        }
        return keys;
    }, [bookedSlots]);
    const dirty =
        durationMinutes !== saved.slotDurationMinutes || !slotsEqual(slots, saved.slots || []);

    const { morningMinutes, afternoonMinutes } = useMemo(() => {
        const all = availabilityStartMinutesForDuration(durationMinutes);
        return {
            morningMinutes: all.filter((minute) => minute < 720),
            afternoonMinutes: all.filter((minute) => minute >= 780),
        };
    }, [durationMinutes]);

    const renderRow = (startMinute: number) => (
        <div key={startMinute} className="msess-grid-row">
            <div className="msess-time">{formatClock(startMinute)}</div>
            {DAYS.map((day) => {
                const key = slotKey(day.dayOfWeek, startMinute);
                const isOpen = open.has(key);
                const isBooked = isOpen && bookedKeys.has(key);
                return (
                    <button
                        key={`${day.dayOfWeek}-${startMinute}`}
                        type="button"
                        className={`msess-cell${isOpen ? ' is-open' : ' is-unavailable'}${
                            isBooked ? ' is-booked' : ''
                        }`}
                        aria-pressed={isOpen}
                        aria-label={`${day.label} ${formatClock(startMinute)}: ${
                            isBooked ? 'Booked' : isOpen ? 'Available' : 'Unavailable'
                        }`}
                        title={
                            isBooked
                                ? 'Booked by a mentee — cannot change until the booking is cleared'
                                : isOpen
                                  ? 'Available — click to mark Unavailable'
                                  : 'Unavailable — click to mark Available'
                        }
                        disabled={isBooked || isSaving}
                        onClick={() => {
                            if (isBooked || isSaving) return;
                            onToggle(day.dayOfWeek, startMinute);
                        }}
                    >
                        {isBooked ? 'Booked' : isOpen ? '✓ Open' : 'Unavailable'}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="msess-avail">
            <div className="alert alert-info border-0 rounded-3 small mb-3" role="note">
                {BANNER}
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                <div>
                    <h6 className="fw-bold text-dark mb-0">Weekly Recurring Availability</h6>
                    <div className="text-secondary small mt-1">Times are shown as Asia/Kolkata (IST).</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex gap-2 small">
                        <span className="d-flex align-items-center gap-1">
                            <span className="badge bg-primary p-2 rounded-circle d-block"></span> Available
                        </span>
                        <span className="d-flex align-items-center gap-1">
                            <span className="badge bg-success p-2 rounded-circle d-block"></span> Booked
                        </span>
                        <span className="d-flex align-items-center gap-1">
                            <span className="badge bg-light border p-2 rounded-circle d-block"></span> Unavailable
                        </span>
                    </div>

                    <select
                        className="form-select form-select-sm font-12 w-auto"
                        value={durationMinutes}
                        onChange={(event) => onDurationChange(Number(event.target.value))}
                    >
                        {DURATIONS.map((value) => (
                            <option key={value} value={value}>
                                {value} min sessions
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="msess-grid" role="grid" aria-label="Weekly recurring availability">
                <div className="msess-grid-row msess-grid-head">
                    <div />
                    {DAYS.map((day) => (
                        <div key={day.dayOfWeek} className="msess-day">
                            {day.label}
                        </div>
                    ))}
                </div>
                {morningMinutes.map(renderRow)}
                <div className="msess-grid-row msess-lunch" aria-hidden="true">
                    <div className="msess-time">12:00 PM</div>
                    <div className="msess-lunch-bar">LUNCH BREAK</div>
                </div>
                {afternoonMinutes.map(renderRow)}
            </div>
            <div className="msess-save-row">
                <button type="button" className="btn btn-primary font-500 rounded-pill px-4" disabled={!dirty || isSaving} onClick={onSave}>
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn btn-outline-secondary font-500 rounded-pill px-3" disabled={!dirty || isSaving} onClick={onDiscard}>
                    Discard
                </button>
                {saveError ? <p className="msess-save-error">{saveError}</p> : null}
            </div>
        </div>
    );
};

export default MentorAvailabilityPanel;
